import React, {
  useMemo,
  ChangeEvent,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import cx from 'classnames';

import {
  parseStringToRichText,
  RichText,
  RichTextEditor,
} from '@vimily/bonjoro-ui-components-v2/RichTextEditor';
import { QuestionnaireInputType } from '.';
import { TextArea } from '@vimily/bonjoro-ui-components-v2/TextArea';
import {
  ColorPickerInput,
  ColorPreviewShape,
} from '~/App/Components/ColorPickerInput';
import { TestimonialResponseType } from '~/types/models/testimonial';
import { Checkbox } from '@vimily/bonjoro-ui-components-v2/Checkbox';
import { questionnaireCardsColors } from '~/App/Components/QuestionnaireCard';
import { isEqual } from 'lodash';

export const questionnaireToolbarClassName =
  'sm:lg:v2-px-1.5 sm:lg:v2-gap-x-0 2xl:v2-gap-x-2.5 2xl:v2-px-2.5';

export interface QuestionItem {
  index: number;
  value: string | RichText;
  color?: string;
  responseTypes?: TestimonialResponseType[];
}

export interface QuestionProps {
  number?: number;
  value?: string | RichText;
  color?: string;
  responseTypes?: TestimonialResponseType[];
  placeholder?: string;
  onUpdate?: (item: QuestionItem) => void;
  onDelete?: (index: number) => void;
  onFocus?: (index: number) => void;
  inputType?: QuestionnaireInputType;
  labelClassName?: string;
  deleteButtonClassName?: string;
  inputClassName?: string;
}

export default function Question({
  number = 1,
  value: defaultValue = '',
  color: defaultColor,
  responseTypes: defaultResponseTypes = [
    TestimonialResponseType.Text,
    TestimonialResponseType.Video,
  ],
  placeholder,
  onUpdate,
  onDelete,
  onFocus,
  inputType = QuestionnaireInputType.TextArea,
  labelClassName,
  deleteButtonClassName,
  inputClassName,
}: QuestionProps) {
  const index = useMemo(() => number - 1, [number]);
  const [value, setValue] = useState<string | RichText>(defaultValue);
  const [color, setColor] = useState<string>(
    defaultColor ?? questionnaireCardsColors[number - 1]
  );
  const [responseTypes, setResponseTypes] =
    useState<TestimonialResponseType[]>(defaultResponseTypes);

  useEffect(() => {
    if (!defaultColor || defaultColor === color) return;
    setColor(defaultColor);
  }, [defaultColor]);

  useEffect(() => {
    if (!defaultValue || isEqual(defaultValue, value)) return;
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (!defaultResponseTypes || isEqual(defaultResponseTypes, responseTypes))
      return;
    setResponseTypes(defaultResponseTypes);
  }, [defaultResponseTypes]);

  useEffect(() => {
    onUpdate?.({
      index,
      value,
      color,
      responseTypes,
    });
  }, [value, color, responseTypes]);

  const textAreaProps = {
    className: cx(inputClassName, 'v2-bg-white v2-text-neutral-900'),
    placeholder,
    onFocus: () => onFocus?.(index),
  };

  const textArea =
    inputType === QuestionnaireInputType.TextArea ? (
      <TextArea
        {...textAreaProps}
        value={(value ?? '') as string}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
          setValue(event.target.value);
        }}
      />
    ) : (
      <RichTextEditor
        {...textAreaProps}
        editorClassName='!v2-min-h-[75px]'
        toolbarGroupClassName={questionnaireToolbarClassName}
        value={(value ?? parseStringToRichText('')) as RichText}
        onChange={(value) => setValue(value)}
      />
    );

  const checkBoxes = [
    {
      label: 'Video Response',
      isChecked: responseTypes.includes(TestimonialResponseType.Video),
      type: TestimonialResponseType.Video,
    },
    {
      label: 'Text Response',
      isChecked: responseTypes.includes(TestimonialResponseType.Text),
      type: TestimonialResponseType.Text,
    },
  ];

  const handleOnSelectResponseType = (
    event: React.ChangeEvent<HTMLInputElement>,
    responseType: TestimonialResponseType
  ) => {
    const updates: TestimonialResponseType[] = event.target.checked
      ? [...responseTypes, responseType]
      : responseTypes.filter((type) => type !== responseType);
    setResponseTypes(updates);
  };

  const onResponseTypeClick = useCallback(() => {
    onFocus?.(index);
  }, [index]);

  return (
    <div className='flex flex-col gap-y-[15px]'>
      <div className='flex justify-between items-center'>
        <p
          className={cx(labelClassName, '!text-base text-white font-bold m-0')}
        >
          Question {number}
        </p>
        {number > 1 && (
          <FontAwesomeIcon
            role='button'
            className={cx(
              deleteButtonClassName,
              'text-white text-sm hover:!v2-text-orange-600'
            )}
            icon={faTrash}
            onClick={() => onDelete?.(index)}
          />
        )}
      </div>
      {textArea}
      <div className='flex flex-col gap-y-2'>
        <p className='text-base text-white font-bold m-0'>Question box color</p>
        <ColorPickerInput
          shape={ColorPreviewShape.Circle}
          value={color}
          onSelect={setColor}
          onFocus={() => onFocus?.(index)}
        />
      </div>
      <div className='flex flex-col gap-y-2'>
        <p className='text-base v2-text-white font-bold'>
          Question {number} response types
        </p>
        <div className='flex flex-col md:flex-row'>
          {checkBoxes.map((checkbox, index) => (
            <Checkbox
              key={index}
              labelTextColorClassName='v2-text-white'
              bgColorClassName='v2-bg-white'
              labelTextSizeClassName='v2-text-base'
              className='flex-1 items-center'
              label={checkbox.label}
              checked={checkbox.isChecked}
              onClick={onResponseTypeClick}
              onChange={(event: ChangeEvent<HTMLInputElement> | undefined) =>
                handleOnSelectResponseType(
                  event as ChangeEvent<HTMLInputElement>,
                  checkbox.type
                )
              }
            />
          ))}
        </div>
      </div>
      <hr className='w-full v2-border-top-[3px] v2-border-white m-0 mb-5' />
    </div>
  );
}
